import React from "react";
import { useT, useLanguage } from "@/lib/LanguageContext";
// Relative: resolvable under Jest without a shim.
import LoadFailure from "../components/ui/LoadFailure";
import { extractApiError } from "../lib/apiErrors";
import { dateLocale } from "../i18n/dateLocale";
import { PLATFORM_OFFERS as IDS } from "../constants/testIds/platformOffers";
import {
  REDEMPTION_STATUS_KEYS,
  REDEMPTION_STATUS_TONES,
  RELEASE_REASON_KEYS,
  SURFACE_KEYS,
  redemptionValueText,
} from "./describe";

/**
 * The practice's platform codes (contract §5.5): what was redeemed, where, and where it
 * stands. A `scheduled` promo reads "starts when the trial ends" (§5.0); a `released`
 * one never discounted anything and is listed so the practitioner can see why the code
 * they typed did not stick.
 *
 * The rows and the read state come from `useTenantRedemptions`, owned by the page so a
 * redeem or a plan change can reload the list.
 *
 * @param {{items: object[], loading: boolean, loadError: any, reload: () => void}} redemptions
 * @param {function} t  translator bound to "admin"
 */
export default function TenantRedemptionsCard({ redemptions, t }) {
  const tc = useT("common");
  const { lang } = useLanguage();
  const { items, loading, loadError, reload } = redemptions;
  const when = (iso) => (iso ? new Date(iso).toLocaleDateString(dateLocale(lang)) : "—");

  return (
    <div className="bg-white rounded-2xl border border-eh-border-soft p-6" data-testid={IDS.billingRedemptions}>
      <h2 className="font-display text-xl text-eh-ink mb-4">{t("bill.offers.redemptionsTitle")}</h2>
      <LoadFailure
        message={loadError ? extractApiError(loadError, tc("errors.loadFailed"), tc) : ""}
        onRetry={reload}
        retryLabel={tc("actions.retry")}
        testId={`${IDS.billingRedemptions}-load-error`}
      />
      {!loadError && !loading && items.length === 0 && (
        <p className="text-sm text-eh-muted" data-testid={`${IDS.billingRedemptions}-empty`}>
          {t("bill.offers.redemptionsEmpty")}
        </p>
      )}
      {!loadError && items.length > 0 && (
        <ul className="divide-y divide-eh-border-soft">
          {items.map((r) => (
            <li
              key={r.redemption_id}
              data-testid={IDS.billingRedemptionRow(r.redemption_id)}
              data-status={r.status}
              className="py-3 flex items-center justify-between gap-4 text-sm"
            >
              <div className="min-w-0">
                <code className="font-semibold text-eh-ink">{r.code}</code>
                <span className="text-eh-muted"> · {redemptionValueText(r, tc, lang)}</span>
                <div className="text-xs text-eh-muted">
                  {tc(SURFACE_KEYS[r.surface])} · {when(r.applied_at || r.held_at)}
                  {r.status === "released" && r.release_reason && ` · ${tc(RELEASE_REASON_KEYS[r.release_reason])}`}
                </div>
              </div>
              <span className={`text-xs rounded-full px-2 py-0.5 whitespace-nowrap ${REDEMPTION_STATUS_TONES[r.status]}`}>
                {tc(REDEMPTION_STATUS_KEYS[r.status])}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
